import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "config/api";
import {
    Card,
    CardContent,
    Typography,
    Icon,
    Tooltip,
    Grid,
    CircularProgress,
    TextField,
    InputLabel,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import safeToast from "utils/safeToast";

// Custom Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { decryptText } from "config/encryption";

const cleanValue = (val) => {
    if (typeof val === "string" && val.includes(":")) {
        const parts = val.split(":");
        return parts.slice(1).join(":").trim();
    }
    return val;
};

const cleanProfileData = (data) => {
    const cleaned = {};
    for (const key in data) {
        if (data.hasOwnProperty(key)) {
            cleaned[key] = cleanValue(data[key]);
        }
    }
    return cleaned;
};

const BasicInfo = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({});
    const [dropdowns, setDropdowns] = useState({
        salutations: [],
        genders: [],
        languages: [],
        bloodGroups: [],
    });

    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDarkMode = theme.palette.mode === "dark";

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const userDetails = localStorage.getItem("UserLogDetails");
                if (!userDetails) throw new Error("No user details");

                const decryptedUser = decryptText(userDetails);
                const parsedUser = JSON.parse(decryptedUser);
                const { StudentId, Token } = parsedUser;

                if (!Token) {
                    safeToast.error("Session expired. Please log in again.");
                    localStorage.clear();
                    return navigate("/BasicAuth");
                }

                const res = await fetch(API_ENDPOINTS.PROFILE.GET_BASIC_INFO(StudentId), {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${Token}`,
                    },
                });

                if (!res.ok) {
                    if (res.status === 401) {
                        safeToast.error("Session expired. Please log in again.");
                        localStorage.clear();
                        return navigate("/BasicAuth");
                    }
                    throw new Error("Failed to load profile");
                }

                const data = await res.json();
                const cleanedData = cleanProfileData(data);
                setProfile(cleanedData);
            } catch (err) {
                safeToast.error("Session expired. Please log in again.");
                localStorage.clear();
                navigate("/BasicAuth");
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [navigate]);

    const fetchDropdowns = async (Token) => {
        const res = await fetch(API_ENDPOINTS.PROFILE.GET_DROPDOWNS, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${Token}`,
            },
        });

        if (!res.ok) throw new Error("Failed to fetch dropdowns");

        const data = await res.json();
        setDropdowns({
            salutations: data.salutations || [],
            genders: data.genders || [],
            languages: data.languages || [],
            bloodGroups: data.bloodGroups || [],
        });
    };

    const handleUpdate = async () => {
        try {
            const userDetails = localStorage.getItem("UserLogDetails");
            const decryptedUser = decryptText(userDetails);
            const { Token } = JSON.parse(decryptedUser);

            await fetchDropdowns(Token);

            setFormData(profile);
            setEditMode(true);
        } catch (error) {
            safeToast.error("Unable to load dropdowns.");
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCancel = () => setEditMode(false);

    const getDropdownIdByText = (dropdownList, selectedText) => {
        const item = dropdownList.find((x) => x.ftxt === selectedText);
        return item?.id || 0;
    };

    const handleSave = async () => {
        try {
            const userDetails = localStorage.getItem("UserLogDetails");
            if (!userDetails) throw new Error("User not logged in");

            const decryptedUser = decryptText(userDetails);
            const parsedUser = JSON.parse(decryptedUser);
            const { Token, StudentId, LoginId } = parsedUser;

            // Map selected text values to dropdown IDs
            let salutationId = getDropdownIdByText(dropdowns.salutations, formData.slt);
            const genderId = getDropdownIdByText(dropdowns.genders, formData.gn);
            const motherTongueId = getDropdownIdByText(dropdowns.languages, formData.mth);
            const bloodGroupId = getDropdownIdByText(dropdowns.bloodGroups, formData.blg);

            // If salutationId is 0 (not found), fallback to 22
            if (salutationId === 0) {
                salutationId = 22;
            }

            const payload = {
                studentId: StudentId,
                salutation: salutationId, // Use computed salutationId here
                rollNum: formData.rn || "",
                studName: formData.sn || "",  // Use 'sn' (StudName) from API response
                lastName: formData.ln || "",  // Use 'ln' (LastName) from API response
                gender: genderId,
                studDOB: formData.dobStr || "",
                motherTongue: motherTongueId,
                blood: bloodGroupId,
                createdUserId: StudentId,
                loginId: LoginId || 0,
            };

            const response = await fetch(
                API_ENDPOINTS.PROFILE.UPDATE_BASIC_INFO,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${Token}`,
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (!response.ok) throw new Error("Update failed");

            safeToast.success("Profile updated successfully!");
            setProfile(formData);
            setEditMode(false);
        } catch (error) {
            console.error(error);
            localStorage.clear();
            setTimeout(() => {
                safeToast.error("Session expired. Redirecting to login...");
                navigate("/BasicAuth");
            }, 4000);
        }
    };

    if (loading) {
        return (
            <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress color="info" />
            </MDBox>
        );
    }

    if (!profile) {
        return <Typography>No profile data found.</Typography>;
    }

    const profileFields = [
        { label: "Roll Number", key: "rn" },
        { label: "Name", key: "sn" },
        { label: "Salutation", key: "slt" },
        { label: "Gender", key: "gn" },
        { label: "Date of Birth", key: "dobStr" },
        { label: "Mother Tongue", key: "mth" },
        { label: "Blood Group", key: "blg" },
        { label: "Contact Number", key: "cn" },
        { label: "Email 1", key: "em1" },
    ];

    return (
        <>
            <MDBox py={2} px={isMobile ? 1 : 3}>
                <Card sx={{ boxShadow: 3, backgroundColor: isDarkMode ? theme.palette.grey[800] : "#fff" }}>
                    <MDBox
                        variant="gradient"
                        borderRadius="lg"
                        coloredShadow="info"
                        mx={1}
                        mt={-2}
                        p={1}
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ backgroundColor: isDarkMode ? theme.palette.grey[900] : "#1976d2", color: "#fff" }}
                    >
                        <MDBox display="flex" alignItems="center">
                            <Tooltip title="Alumni Basic Info">
                                <Icon sx={{ mr: 1 }}>school</Icon>
                            </Tooltip>
                            <MDTypography variant="body2" fontWeight="medium" color="white">
                                {editMode ? "Update Basic Details" : "Alumni Basic Info"}
                            </MDTypography>
                        </MDBox>
                        {!editMode && (
                            <Tooltip title="Edit Profile">
                                <MDButton variant="outlined" color="light" onClick={handleUpdate}>
                                    <Icon>edit</Icon>
                                </MDButton>
                            </Tooltip>
                        )}
                    </MDBox>

                    <CardContent>
                        <Grid container spacing={3}>
                            {profileFields.map(({ label, key }, idx) => {
                                if (editMode && (key === "cn" || key === "em1")) return null; // hide in edit mode

                                return (
                                    <Grid item xs={12} sm={6} key={idx}>
                                        <MDBox mb={2}>
                                            <InputLabel htmlFor={key} sx={{ fontWeight: "bold" }}>
                                                {label}
                                            </InputLabel>
                                            {editMode ? (
                                                ["slt", "gn", "mth", "blg"].includes(key) ? (
                                                    <TextField
                                                        id={key}
                                                        select
                                                        SelectProps={{ native: true }}
                                                        name={key}
                                                        value={formData[key] || ""}
                                                        onChange={handleChange}
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                    >
                                                        {(key === "slt"
                                                            ? dropdowns.salutations
                                                            : key === "gn"
                                                                ? dropdowns.genders
                                                                : key === "mth"
                                                                    ? dropdowns.languages
                                                                    : dropdowns.bloodGroups
                                                        ).map((item) => (
                                                            <option key={item.id} value={item.ftxt}>
                                                                {item.ftxt}
                                                            </option>
                                                        ))}
                                                    </TextField>
                                                ) : (
                                                    <TextField
                                                        id={key}
                                                        name={key}
                                                        value={formData[key] || ""}
                                                        onChange={handleChange}
                                                        disabled={key === "sn" || key === "rn"}
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )
                                            ) : (
                                                <Typography variant="body2">{profile[key] || "—"}</Typography>
                                            )}
                                        </MDBox>
                                    </Grid>
                                );
                            })}

                        </Grid>

                        {editMode && (
                            <MDBox display="flex" justifyContent="flex-end" mt={3}>
                                <MDButton variant="contained" color="info" onClick={handleSave} sx={{ mr: 1 }}>
                                    Save
                                </MDButton>
                                <MDButton variant="outlined" color="secondary" onClick={handleCancel}>
                                    Cancel
                                </MDButton>
                            </MDBox>
                        )}
                    </CardContent>
                </Card>
            </MDBox>
        </>
    );
};

export default BasicInfo;
